import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import { ModalProps, ModalView } from '../../../components/organisms/Modal'
import { getRealmConfigurationFn, updateRealmConfigurationFn } from '../../../api'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'
import Button from 'components/molecules/Button'
import InputWithCaptions from '../../../components/molecules/InputWithCaptions'
import { RealmConfiguration } from '../../../@types'
import Loader from '../../../components/molecules/Loader'
import Tabs, { Tab } from '../../../components/molecules/Tabs'
import { chunk, fromPairs, isString } from 'lodash'


const EditRealmConfiguration: FC<ModalProps> = ({ onClose })  => {

  const [t] = useTranslation()
  const queryClient = useQueryClient()

  const [currentTab, setCurrentTab] = React.useState("variables")

  const params = useParams<{ id: string }>()
  const i_realm = +params.id!

  const {
    control,
    formState: { isValid },
    setValue,
    handleSubmit,
  } = useForm<Record<string, any>>()

  const { data: selectedRealmConfiguration } = useQuery(['realm_configuration', i_realm], getRealmConfigurationFn, {
    select: (data) => data || {}
  })

  const { mutate: updateRealmMailSettings } = useMutation((data: Partial<RealmConfiguration>) => updateRealmConfigurationFn({ ...data }), {
    onSuccess: () => {
      queryClient.invalidateQueries('realm_configuration')
      onClose()
    },
  })

  const onSubmit = (values: Record<string, unknown>) => {
    const { template_variables, ldap_settings } = values as {
      template_variables: Record<string, string>,
      ldap_settings: Record<string, any>
    }

    const { Roster } = ldap_settings.mapping
    delete ldap_settings.mapping.Roster // remove Roster mapping if exists

    updateRealmMailSettings({
      ...selectedRealmConfiguration,
      template_variables: template_variables,
      ldap_settings: {
        ...ldap_settings,
        ldap_attributes: isString(ldap_settings.ldap_attributes) ? ldap_settings.ldap_attributes.split(',').map((item: string) => item.trim()) : ldap_settings.ldap_attributes,
        mapping: {
          ...((ldap_settings as { mapping?: Record<string, unknown> }).mapping || {}),
          ...fromPairs(Object.entries(Roster||{}).map(([key, value]) => [`Roster.${key}`, value]))
        },
      }, i_realm })
  }

  if (!selectedRealmConfiguration) {
    return <Loader height="30vh" />
  }

  return (
    <ModalView title={t('page.realms.configure')} onClose={onClose}>
        <Form onSubmit={handleSubmit(onSubmit)}>

          <Tabs activeTab={String(currentTab)} onSwitch={setCurrentTab}>
            <Tab key={`variables_template`} label={t('page.realms.variables')} value={'variables'} />
            <Tab key={`ldap_settings`} label={t('page.realms.ldap')} value={'ldap'} />
          </Tabs>
          <Content>

            {
              String(currentTab) === 'variables' && (
                Object.entries(selectedRealmConfiguration.template_variables as Record<string, string>).map(([key, value]) => (<Field key={key}>
                    <Controller
                      name={`template_variables.${key}`}
                      control={control}
                      defaultValue={value}
                      render={({ field: { ref, ...props } }) => (
                        <InputWithCaptions type="text" caption={key} {...props} />
                      )}
                    />
                  </Field>))
              )
            }

            <div style={{ display: String(currentTab) === 'ldap' ? 'flex' : 'none', flexDirection: 'column' }}>
                <Field>
                  <Controller
                    name={'ldap_settings.ldap_server'}
                    control={control}
                    defaultValue={selectedRealmConfiguration.ldap_settings?.ldap_server || ''}
                    render={({ field: { ref, ...props } }) => (
                      <InputWithCaptions type="text" caption={t('page.realms.ldap_server')} {...props} />
                    )}
                  />
                  <Controller
                    name={'ldap_settings.ldap_port'}
                    control={control}
                    defaultValue={selectedRealmConfiguration.ldap_settings?.ldap_port || ''}
                    render={({ field: { ref, ...props } }) => (
                      <InputWithCaptions type="text" caption={t('page.realms.ldap_port')} {...props} />
                    )}
                  />
                </Field>
                <Field>
                  <Controller
                    name={'ldap_settings.ldap_bind_dn'}
                    control={control}
                    defaultValue={selectedRealmConfiguration.ldap_settings?.ldap_bind_dn || ''}
                    render={({ field: { ref, ...props } }) => (
                      <InputWithCaptions type="text" caption={t('page.realms.ldap_bind_dn')} {...props} />
                    )}
                  />
                  <Controller
                    name={'ldap_settings.ldap_bind_password'}
                    control={control}
                    defaultValue={selectedRealmConfiguration.ldap_settings?.ldap_bind_password || ''}
                    render={({ field: { ref, ...props } }) => (
                      <InputWithCaptions type="text" caption={t('page.realms.ldap_bind_password')} {...props} />
                    )}
                  />
                </Field>
                  <Field>
                    <Controller
                      name={'ldap_settings.ldap_search_base'}
                      control={control}
                      defaultValue={selectedRealmConfiguration.ldap_settings?.ldap_search_base || ''}
                      render={({ field: { ref, ...props } }) => (
                        <InputWithCaptions type="text" caption={t('page.realms.ldap_search_base')} {...props} />
                      )}
                    />
                    <Controller
                      name={'ldap_settings.ldap_search_filter'}
                      control={control}
                      defaultValue={selectedRealmConfiguration.ldap_settings?.ldap_search_filter || ''}
                      render={({ field: { ref, ...props } }) => (
                        <InputWithCaptions type="text" caption={t('page.realms.ldap_search_filter')} {...props} />
                      )}
                    />
                  </Field>
                  <Field>
                    <Controller
                      name={'ldap_settings.ldap_attributes'}
                      control={control}
                      defaultValue={((selectedRealmConfiguration.ldap_settings?.ldap_attributes || []) as string[]).join(',')}
                      render={({ field: { ref, ...props } }) => (
                        <InputWithCaptions type="text" caption={t('page.realms.ldap_attributes')} {...props} onChange={(e) => {
                          const value = e.target.value.split(',').map((item) => item.trim())
                          setValue(props.name, value, {
                            shouldValidate: true, // trigger validation
                            shouldTouch: true, // update touched fields form state
                          })
                        }} />
                      )}
                    />
                  </Field>
                  <hr/>
                  {t('page.realms.ldap_mapping')}:
                  {
                    chunk(Object.entries(selectedRealmConfiguration.ldap_settings.mapping || {}), 2).map((items) => {
                      return (
                        <Field key={Math.random().toString(20)}>
                          {
                          items.map(([key, value]) => {
                            return (
                              <Controller
                                key={key}
                                name={`ldap_settings.mapping.${key}`}
                                control={control}
                                defaultValue={value}
                                render={({ field: { ref, ...props } }) => (
                                  <InputWithCaptions type="text" caption={key} {...props} />
                                )}
                              />
                            )
                          })
                          }
                        </Field>
                      )
                    })
                  }
              </div>

          </Content>
          <SubmitButton appearance="blue" type="submit" disabled={!isValid}>
            {t('modal.addRealm.save')}
          </SubmitButton>
        </Form>


    </ModalView>
  )
}

export default EditRealmConfiguration


const SubmitButton = styled(Button)`
  margin-top: 32px;
  align-self: flex-end;
`

const Content = styled.div`
  overflow-y: scroll;
  height: 50vh;
  width: 100%;
  padding: 10px 0 0 0;
`

const Form = styled.form`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
`
const Field = styled.div`
  display: flex;
  margin-bottom: 16px;
  flex-flow: wrap;
  gap: 12px;
  width: 100%;
  & > * {
    flex: 1;
    min-width: 180px;
  }
`